import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  AcademicCapIcon,
  ChartBarIcon,
  DocumentTextIcon,
  UserGroupIcon,
  StarIcon,
  FlagIcon,
  PencilIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import {
  ProgramsService,
  ProgressService,
  SubmissionsService,
  PROGRAM_TYPES,
} from '../../services/programsService';
import ProgressTracker from './ProgressTracker';
import SubmissionManager from './SubmissionManager';
import ModuleViewer from './ModuleViewer';
import AdminPanel from './AdminPanel';
import ProgramHealthCheckDashboard from './ProgramHealthCheckDashboard';
import { firestore } from '../../database/firebase';
import { collection, getDocs, getCountFromServer, query, where } from 'firebase/firestore';

const PROGRAM_CONTENT = {
  [PROGRAM_TYPES.DESIGN_A_BUSINESS]: {
    title: 'Design-A-Business',
    description:
      'Master essential business design concepts and tools for creating successful ventures',
    modules: [
      {
        id: 1,
        title: 'Module 1: Define Your Vision and Opportunity',
        summary: 'Clarify your mission, problem-solution fit, and business opportunity.',
        objective:
          'Clarify your mission, problem-solution fit, and business opportunity around your product.',
        topics: [
          'Defining your founder vision and "why"',
          "Understanding the problem you're solving",
          'Opportunity analysis using PESTLE and SWOT',
          'Value proposition design',
        ],
        deliverables: [
          "Founder's Vision Statement",
          'Opportunity Brief',
          'Value Proposition Canvas',
        ],
        duration: '2 hours',
        type: 'interactive',
        category: 'Foundation',
        difficulty: 'Beginner',
        rating: 4.8,
        participants: 45,
      },
      {
        id: 2,
        title: 'Module 2: Know Your Customer',
        summary: 'Identify and deeply understand your ideal customers and their buying behavior.',
        objective: 'Identify and deeply understand your ideal customers and their buying behavior.',
        topics: [
          'Customer personas & segmentation',
          'Jobs-to-be-done framework',
          'Customer interviews & surveys',
          'Early adopter vs. mainstream market',
        ],
        deliverables: [
          '2–3 Customer Personas',
          'Customer Journey Map',
          'Interview Summary & Insights',
        ],
        duration: '3 hours',
        type: 'document',
        category: 'Research',
        difficulty: 'Beginner',
        rating: 4.7,
        participants: 42,
      },
      {
        id: 3,
        title: 'Module 3: Design a Scalable Business Model',
        summary: 'Build a clear, lean business model that aligns with your goals and product.',
        objective: 'Build a clear, lean business model that aligns with your goals and product.',
        topics: [
          'Business Model Canvas (BMC)',
          'Revenue models (subscription, licensing, freemium, etc.)',
          'Key resources, partners, and activities',
          'Product–market fit',
        ],
        deliverables: ['Completed Business Model Canvas', 'Revenue Model Plan'],
        duration: '4 hours',
        type: 'interactive',
        category: 'Strategy',
        difficulty: 'Intermediate',
        rating: 4.6,
        participants: 38,
      },
      {
        id: 4,
        title: 'Module 4: Build Your Go-to-Market Strategy',
        summary: 'Design a realistic and resource-sensitive GTM plan.',
        objective: 'Design a realistic and resource-sensitive GTM plan.',
        topics: [
          'Customer acquisition channels',
          'Brand positioning & messaging',
          'Sales strategy (B2B, B2C, D2C)',
          'Launch planning and timeline',
        ],
        deliverables: [
          'Go-to-Market (GTM) Strategy',
          'Launch Campaign Brief',
          'Marketing Funnel Plan',
        ],
        duration: '3.5 hours',
        type: 'video',
        category: 'Marketing',
        difficulty: 'Intermediate',
        rating: 4.5,
        participants: 35,
      },
      {
        id: 5,
        title: 'Module 5: Set Up Operations and Finances',
        summary: 'Lay the foundation for smooth business operations and financial clarity.',
        objective: 'Lay the foundation for smooth business operations and financial clarity.',
        topics: [
          'Basic legal & compliance structure',
          'Supply chain or service delivery processes',
          'Pricing strategies',
          'Budgeting, unit economics & break-even',
          'Funding options & financial projections',
        ],
        deliverables: ['Operational Plan', 'Pricing Strategy', '12-Month Cash Flow Forecast'],
        duration: '4 hours',
        type: 'document',
        category: 'Operations',
        difficulty: 'Advanced',
        rating: 4.4,
        participants: 32,
      },
      {
        id: 6,
        title: 'Module 6: Pitch, Plan and Launch',
        summary: 'Pull everything together into a compelling investor pitch and action plan.',
        objective: 'Pull everything together into a compelling investor pitch and action plan.',
        topics: [
          'Building a pitch deck (Problem, Solution, Market, Model, Team, Ask)',
          'Business planning for scale',
          'Setting milestones & KPIs',
          'Practicing the pitch',
        ],
        deliverables: ['Pitch Deck', 'Business Summary Document', '90-Day Execution Plan'],
        duration: '2.5 hours',
        type: 'interactive',
        category: 'Presentation',
        difficulty: 'Advanced',
        rating: 4.3,
        participants: 30,
      },
    ],
    lessons: [
      {
        id: 1,
        title: 'Understanding Vision and Opportunity',
        description: 'Introduction to defining your vision and identifying business opportunities.',
        type: 'video',
        duration: '45 minutes',
        difficulty: 'Beginner',
        rating: 4.8,
        content: 'Covers vision, mission, and opportunity analysis for new ventures.',
        resources: [
          { title: 'Vision Statement Template', url: '#' },
          { title: 'Opportunity Analysis Guide', url: '#' },
        ],
      },
      {
        id: 2,
        title: 'Customer Discovery and Personas',
        description: 'Learn how to identify and understand your ideal customer.',
        type: 'document',
        duration: '60 minutes',
        difficulty: 'Beginner',
        rating: 4.7,
        content: 'Covers customer interviews, personas, and journey mapping.',
        resources: [
          { title: 'Persona Worksheet', url: '#' },
          { title: 'Customer Interview Tips', url: '#' },
        ],
      },
      {
        id: 3,
        title: 'Business Model Design',
        description: 'Explore business model canvas and revenue models.',
        type: 'interactive',
        duration: '90 minutes',
        difficulty: 'Intermediate',
        rating: 4.7,
        content: 'Learn to use the Business Model Canvas and plan revenue streams.',
        resources: [
          { title: 'BMC Template', url: '#' },
          { title: 'Revenue Model Examples', url: '#' },
        ],
      },
    ],
  },
  [PROGRAM_TYPES.COSTING_ACCOUNTING]: {
    title: 'Introduction to Costing and Management Accounting',
    description:
      'Master essential financial management concepts and tools for better business decision-making and financial health',
    modules: [
      {
        id: 1,
        title: 'Financial Performance Assessment',
        description:
          "Helps entrepreneurs assess how well they understand their business's financial health using a scoring system.",
        type: 'interactive',
        duration: '2.5 hours',
        participants: 45,
        rating: 4.8,
        category: 'Foundation',
        difficulty: 'Beginner',
        prerequisites: 'None',
        lastUpdated: '2024-01-15',
      },
      {
        id: 2,
        title: 'Cost of Sales, Mark-Up, Gross Profit & Margin',
        description:
          'Teaches how to calculate and monitor the cost of sales, set markups, and understand gross profit and profit margins for products and services.',
        type: 'document',
        duration: '3 hours',
        participants: 42,
        rating: 4.6,
        category: 'Core Concepts',
        difficulty: 'Intermediate',
        prerequisites: 'Financial Performance Assessment',
        lastUpdated: '2024-01-18',
      },
      {
        id: 3,
        title: 'Breakeven Point & Safety Margin',
        description:
          'Explains how to calculate the sales needed to cover all expenses (break even) and how much sales can drop before a loss occurs (safety margin).',
        type: 'interactive',
        duration: '4 hours',
        participants: 38,
        rating: 4.7,
        category: 'Advanced',
        difficulty: 'Advanced',
        prerequisites: 'Cost of Sales, Mark-Up, Gross Profit & Margin',
        lastUpdated: '2024-01-20',
      },
      {
        id: 4,
        title: 'Benchmarking Expenses',
        description:
          'Provides methods to evaluate business expenses against budgets or industry norms to identify and control unnecessary spending.',
        type: 'document',
        duration: '3.5 hours',
        participants: 35,
        rating: 4.5,
        category: 'Advanced',
        difficulty: 'Advanced',
        prerequisites: 'Breakeven Point & Safety Margin',
        lastUpdated: '2024-01-22',
      },
      {
        id: 5,
        title: 'Fixed & Variable Expenses',
        description:
          'Defines types of expenses and how they behave with changes in business activity, aiding in strategic cost management.',
        type: 'video',
        duration: '2.5 hours',
        participants: 40,
        rating: 4.9,
        category: 'Management',
        difficulty: 'Intermediate',
        prerequisites: 'Benchmarking Expenses',
        lastUpdated: '2024-01-25',
      },
      {
        id: 6,
        title: 'Creditor & Debtor Trading Cycles',
        description:
          'Focuses on managing how long it takes to pay suppliers (creditors) and collect payments from customers (debtors).',
        type: 'interactive',
        duration: '3 hours',
        participants: 37,
        rating: 4.4,
        category: 'Advanced',
        difficulty: 'Advanced',
        prerequisites: 'Fixed & Variable Expenses',
        lastUpdated: '2024-01-28',
      },
      {
        id: 7,
        title: 'Profitability vs. Cash Flow',
        description:
          'Clarifies the difference between profit and actual cash on hand, highlighting the importance of managing cash flow effectively.',
        type: 'document',
        duration: '2.5 hours',
        participants: 39,
        rating: 4.6,
        category: 'Management',
        difficulty: 'Intermediate',
        prerequisites: 'Creditor & Debtor Trading Cycles',
        lastUpdated: '2024-01-30',
      },
    ],
    lessons: [
      {
        id: 1,
        title: 'Understanding Financial Performance Metrics',
        description: 'Introduction to key financial health indicators and scoring systems',
        type: 'video',
        duration: '45 minutes',
        difficulty: 'Beginner',
        rating: 4.8,
        content:
          'This lesson covers the fundamental concepts of financial performance assessment, including key metrics, scoring methodologies, and health indicators for business evaluation.',
        resources: [
          { title: 'Financial Performance Fundamentals PDF', url: '#' },
          { title: 'Health Scoring Chart', url: '#' },
        ],
      },
      {
        id: 2,
        title: 'Cost Analysis and Pricing Strategies',
        description: 'Learn cost calculation methods and pricing optimization techniques',
        type: 'document',
        duration: '60 minutes',
        difficulty: 'Beginner',
        rating: 4.6,
        content:
          'Explore various cost analysis methods including cost of sales, markup calculations, and profit margin optimization strategies.',
        resources: [
          { title: 'Cost Analysis Guide', url: '#' },
          { title: 'Pricing Strategy Examples', url: '#' },
        ],
      },
      {
        id: 3,
        title: 'Breakeven Analysis and Risk Assessment',
        description: 'Understanding breakeven points and safety margins for business planning',
        type: 'interactive',
        duration: '90 minutes',
        difficulty: 'Intermediate',
        rating: 4.7,
        content:
          'Learn how to perform breakeven analysis to understand profitability thresholds and assess business risk through safety margin calculations.',
        resources: [
          { title: 'Breakeven Analysis Calculator', url: '#' },
          { title: 'Risk Assessment Template', url: '#' },
        ],
      },
    ],
  },
  [PROGRAM_TYPES.SCALING_MODULES]: {
    title: 'Business Acceleration',
    description:
      'Accelerate your business growth with proven scaling strategies and operational excellence',
    modules: [
      {
        id: 1,
        title: 'Acceleration Strategy & Scale Readiness Check',
        description:
          'Assess your business readiness for scaling and develop a comprehensive acceleration strategy.',
        duration: '3 hours',
        participants: 28,
        rating: 4.7,
        type: 'interactive',
        category: 'Strategy',
        difficulty: 'Intermediate',
        prerequisites: 'Business Fundamentals',
        lastUpdated: '2024-01-15',
      },
      {
        id: 2,
        title: 'Lead Generation Engine',
        description:
          'Build a systematic approach to generating qualified leads for your business growth.',
        duration: '4 hours',
        participants: 35,
        rating: 4.8,
        type: 'video',
        category: 'Marketing',
        difficulty: 'Intermediate',
        prerequisites: 'Acceleration Strategy',
        lastUpdated: '2024-01-18',
      },
      {
        id: 3,
        title: 'Lead Nurturing & Conversion Mastery',
        description: 'Master the art of nurturing leads and converting them into loyal customers.',
        duration: '3.5 hours',
        participants: 32,
        rating: 4.6,
        type: 'interactive',
        category: 'Sales',
        difficulty: 'Advanced',
        prerequisites: 'Lead Generation Engine',
        lastUpdated: '2024-01-20',
      },
      {
        id: 4,
        title: 'Customer Profiling & Retargeting',
        description:
          'Create detailed customer profiles and implement effective retargeting strategies.',
        duration: '2.5 hours',
        participants: 29,
        rating: 4.5,
        type: 'document',
        category: 'Analytics',
        difficulty: 'Intermediate',
        prerequisites: 'Lead Nurturing',
        lastUpdated: '2024-01-22',
      },
      {
        id: 5,
        title: 'Increasing Average Transaction Value (ATV)',
        description:
          'Strategies to increase the value of each customer transaction and maximize revenue.',
        duration: '3 hours',
        participants: 31,
        rating: 4.7,
        type: 'video',
        category: 'Revenue',
        difficulty: 'Advanced',
        prerequisites: 'Customer Profiling',
        lastUpdated: '2024-01-25',
      },
      {
        id: 6,
        title: 'Boosting Frequency of Purchase',
        description:
          'Techniques to encourage repeat purchases and increase customer lifetime value.',
        duration: '2.5 hours',
        participants: 27,
        rating: 4.4,
        type: 'interactive',
        category: 'Retention',
        difficulty: 'Intermediate',
        prerequisites: 'ATV Strategies',
        lastUpdated: '2024-01-28',
      },
    ],
    lessons: [
      {
        id: 1,
        title: 'Scaling Readiness Assessment',
        description: 'Evaluate your business readiness for scaling with a structured assessment.',
        type: 'interactive',
        duration: '45 minutes',
        difficulty: 'Intermediate',
        rating: 4.7,
        content: 'Covers key factors for scale readiness and strategic planning.',
        resources: [
          { title: 'Readiness Checklist', url: '#' },
          { title: 'Strategy Template', url: '#' },
        ],
      },
      {
        id: 2,
        title: 'Lead Generation Fundamentals',
        description: 'Learn the basics of building a lead generation engine.',
        type: 'video',
        duration: '60 minutes',
        difficulty: 'Intermediate',
        rating: 4.8,
        content: 'Covers lead generation strategies and tools for scaling.',
        resources: [{ title: 'Lead Gen Guide', url: '#' }],
      },
    ],
  },
  [PROGRAM_TYPES.CAPACITY_BUILDING]: {
    title: 'Capacity Building Program for Officials',
    description:
      'Equip employees with the skills, tools, and frameworks needed to design, implement, monitor, and evaluate high-impact entrepreneurship development programs.',
    modules: [
      {
        id: 1,
        title: 'Understanding Entrepreneurship Ecosystems',
        keyTopics: ['Role of government', 'Types of entrepreneurs', 'Ecosystem mapping'],
        duration: '1 day',
        format: 'Workshop + Case Studies',
        category: 'Foundation',
        difficulty: 'Beginner',
        rating: 4.6,
        participants: 25,
      },

      {
        id: 2,
        title: 'Program Design & Policy Alignment',
        keyTopics: [
          'Designing results-based programs',
          'Aligning with NDP, SDGs, and National Vision',
          'Inclusive policy considerations',
        ],
        duration: '2 days',
        format: 'Interactive Workshop + Policy Labs',
        category: 'Strategy',
        difficulty: 'Intermediate',
        rating: 4.7,
        participants: 22,
      },
      {
        id: 3,
        title: 'Entrepreneurial Mindsets & Business Lifecycles',
        keyTopics: [
          'Lean thinking',
          'Design thinking',
          'Business lifecycle stages',
          'Understanding founders',
        ],
        duration: '1 day',
        format: 'E-learning + Group Activities',
        category: 'Mindset',
        difficulty: 'Beginner',
        rating: 4.5,
        participants: 28,
      },
      {
        id: 4,
        title: 'Coaching and Mentorship for Entrepreneurs',
        keyTopics: [
          'Difference between advising, coaching, mentoring',
          'Coaching models',
          'Case management approach',
        ],
        duration: '2 days',
        format: 'Role Play + Practicals',
        category: 'Skills',
        difficulty: 'Intermediate',
        rating: 4.8,
        participants: 20,
      },
      {
        id: 5,
        title: 'Client Monitoring & Evaluation (M&E)',
        keyTopics: [
          'KPI design for entrepreneurs',
          'Setting outcomes vs outputs',
          'Use of digital M&E tools',
        ],
        duration: '2 days',
        format: 'Hands-on LMS/CRM Simulation',
        category: 'Evaluation',
        difficulty: 'Advanced',
        rating: 4.4,
        participants: 18,
      },
      {
        id: 6,
        title: 'Data-Driven Program Delivery',
        keyTopics: ['Dashboards', 'Decision-making based on M&E', 'Building feedback loops'],
        duration: '1 day',
        format: 'Practical Lab (using platform analytics)',
        category: 'Analytics',
        difficulty: 'Advanced',
        rating: 4.6,
        participants: 24,
      },
      {
        id: 7,
        title: 'Stakeholder Engagement & Ecosystem Coordination',
        keyTopics: [
          'Collaborating with incubators, funders, and private sector',
          'Public-private models',
          'Referral mechanisms',
        ],
        duration: '1 day',
        format: 'Group Simulation',
        category: 'Collaboration',
        difficulty: 'Intermediate',
        rating: 4.3,
        participants: 26,
      },
      {
        id: 8,
        title: 'Digital Tools for Program Delivery',
        keyTopics: [
          'Use of Client Management System (CMS)',
          'Reports, alerts, tracking modules',
          'Troubleshooting',
        ],
        duration: '2 days',
        format: 'Platform Demo + Staff Certification',
        category: 'Technology',
        difficulty: 'Intermediate',
        rating: 4.7,
        participants: 21,
      },
      {
        id: 9,
        title: 'Inclusive & Gender-Sensitive Program Delivery',
        keyTopics: ['Supporting women, youth, and rural entrepreneurs', 'Accessibility and equity'],
        duration: '1 day',
        format: 'Workshop + Community Examples',
        category: 'Inclusion',
        difficulty: 'Beginner',
        rating: 4.5,
        participants: 23,
      },
      {
        id: 10,
        title: 'Personal Leadership & Change Management',
        keyTopics: [
          'Leading change in public sector',
          'Managing mindset shift',
          'Accountability and resilience',
        ],
        duration: '1 day',
        format: 'Leadership Lab',
        category: 'Leadership',
        difficulty: 'Advanced',
        rating: 4.4,
        participants: 19,
      },
      {
        id: 11,
        title: 'Developing a Knowledge Bank',
        keyTopics: [
          'Institutional memory',
          'Knowledge repositories',
          'Content curation',
          'Version control',
        ],
        duration: '1 day',
        format: 'Workshop + Hands-on Wiki Setup',
        category: 'Operations',
        difficulty: 'Intermediate',
        rating: 4.6,
        participants: 24,
        activity:
          'Design a digital folder structure or wiki-based Knowledge Bank with categorized folders (e.g., M&E reports, success stories, tools).',
        facilitatorTip:
          'Emphasize capturing field experiences and case studies to inform future programming.',
      },
    ],
    lessons: [
      {
        id: 1,
        title: 'Ecosystem Mapping Fundamentals',
        description: 'Learn to map and understand entrepreneurship ecosystems',
        type: 'workshop',
        duration: '4 hours',
        difficulty: 'Beginner',
        rating: 4.6,
        content:
          'Understanding the role of government and different types of entrepreneurs in ecosystem development.',
        resources: [
          { title: 'Ecosystem Mapping Guide', url: '#' },
          { title: 'Government Role Framework', url: '#' },
        ],
      },
      {
        id: 2,
        title: 'Program Design Principles',
        description: 'Design effective entrepreneurship development programs',
        type: 'interactive',
        duration: '6 hours',
        difficulty: 'Intermediate',
        rating: 4.7,
        content: 'Learn to design results-based programs aligned with national policies and goals.',
        resources: [
          { title: 'Program Design Template', url: '#' },
          { title: 'Policy Alignment Checklist', url: '#' },
        ],
      },
    ],
  },
};

const ProgramsManager = ({
  userRole = 'client',
  programType = PROGRAM_TYPES.DESIGN_A_BUSINESS,
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [userProgress, setUserProgress] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [averageRating, setAverageRating] = useState(0);
  const [participantsCount, setParticipantsCount] = useState(0);

  const isClientCapacityBlocked =
    userRole === 'client' && programType === PROGRAM_TYPES.CAPACITY_BUILDING;

  const originalContent =
    PROGRAM_CONTENT[programType] || PROGRAM_CONTENT[PROGRAM_TYPES.DESIGN_A_BUSINESS];

  useEffect(() => {
    const initializeProgram = async () => {
      try {
        setLoading(true);

        if (isClientCapacityBlocked) {
          setSelectedProgram(null);
          setModules([]);
          setLoading(false);
          return;
        }

        const programData = {
          id: programType,
          title: originalContent.title,
          description: originalContent.description,
          type: programType,
          modules: originalContent.modules,
          lessons: originalContent.lessons,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        await ProgramsService.createProgram(programData);

        setSelectedProgram(programData);
        setModules(originalContent.modules);

        if (user) {
          const progress = await ProgressService.getUserProgress(user.uid, programType);
          setUserProgress(prev => ({ ...prev, [programType]: progress }));
        }

        const programSubmissions = await SubmissionsService.getSubmissionsByProgram(programType);
        setSubmissions(programSubmissions);
      } catch (err) {
        toast.error('Error initializing program');
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeProgram();
  }, [programType, user, isClientCapacityBlocked]);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const collectionsToCount = ['clients', 'coaches', 'moderator', 'admin', 'alumni'];
        const counts = await Promise.all(
          collectionsToCount.map(name => getCountFromServer(collection(firestore, name)))
        );
        const totalParticipants = counts.reduce((sum, snap) => sum + (snap.data().count || 0), 0);
        setParticipantsCount(totalParticipants);

        const ratingsQ = query(
          collection(firestore, 'programRatings'),
          where('program', '==', programType)
        );
        const ratingSnap = await getDocs(ratingsQ);
        if (!ratingSnap.empty) {
          let sum = 0;
          let n = 0;
          ratingSnap.forEach(doc => {
            const r = Number(doc.data()?.rating);
            if (!isNaN(r)) {
              sum += r;
              n += 1;
            }
          });
          setAverageRating(n > 0 ? Number((sum / n).toFixed(1)) : 0);
        } else {
          setAverageRating(0);
        }
      } catch (e) {
        setParticipantsCount(0);
        setAverageRating(0);
        toast.error('Error loading stats');
      }
    };
    loadStats();
  }, [programType]);

  useEffect(() => {
    if (!user || !selectedProgram) return;

    const unsubscribeProgress = ProgressService.listenToUserProgress(
      user.uid,
      selectedProgram.id,
      progress => {
        setUserProgress(prev => ({ ...prev, [selectedProgram.id]: progress }));
      }
    );

    const unsubscribeSubmissions = SubmissionsService.listenToSubmissions(
      selectedProgram.id,
      submissions => {
        setSubmissions(submissions);
      }
    );

    return () => {
      unsubscribeProgress();
      unsubscribeSubmissions();
    };
  }, [user, selectedProgram]);

  const handleLessonStart = async lessonId => {
    if (!user || !selectedProgram) return;

    try {
      await ProgressService.startLesson(user.uid, selectedProgram.id, lessonId);
      toast.success('Lesson started successfully!');
    } catch (error) {
      toast.error('Failed to start lesson');
    }
  };

  const handleLessonComplete = async lessonId => {
    if (!user || !selectedProgram) return;

    try {
      await ProgressService.completeLesson(user.uid, selectedProgram.id, lessonId);
      toast.success('Lesson completed successfully!');
    } catch (error) {
      toast.error('Failed to complete lesson');
    }
  };

  const handleSubmissionCreate = async submissionData => {
    if (!user || !selectedProgram) return;

    try {
      await SubmissionsService.createSubmission(
        user.uid,
        selectedProgram.id,
        submissionData,
        submissionData.file
      );
      toast.success('Submission uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload submission');
    }
  };
  const handleSubmissionUpdate = async (submissionId, updates) => {
    try {
      await SubmissionsService.updateSubmission(submissionId, updates);
      toast.success('Submission updated successfully!');
    } catch (error) {
      toast.error('Failed to update submission');
    }
  };

  const calculateOverallProgress = () => {
    if (!userProgress[selectedProgram?.id]) return 0;
    const progress = userProgress[selectedProgram.id];
    return progress.progressPercentage || 0;
  };

  const getProgramStats = () => {
    const totalModules = originalContent.modules.length;
    const totalLessons = originalContent.lessons.length;
    const completedModules = userProgress[selectedProgram?.id]?.completedModules || 0;
    const completedLessons = userProgress[selectedProgram?.id]?.completedLessons || 0;

    return {
      totalModules,
      completedModules,
      totalLessons,
      completedLessons,
      averageRating,
      totalParticipants: participantsCount,
      activeParticipants: participantsCount,
    };
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading program...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mb-4 text-xl text-red-600">Error loading program</div>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const stats = getProgramStats();
  const tabs = [
    { id: 'overview', name: 'Course Overview', icon: AcademicCapIcon },
    { id: 'progress', name: 'Progress Tracker', icon: ChartBarIcon },
    { id: 'modules', name: 'Training Modules', icon: DocumentTextIcon },
    { id: 'submissions', name: 'Submissions', icon: PencilIcon },
    { id: 'healthCheck', name: 'Program Health Check', icon: FlagIcon },
  ];

  const getVisibleTabs = () => {
    switch (userRole) {
      case 'client':
        return tabs.filter(tab => ['overview', 'progress', 'modules'].includes(tab.id));
      case 'coach':
        return tabs.filter(tab =>
          ['overview', 'progress', 'modules', 'submissions', 'healthCheck'].includes(tab.id)
        );
      case 'moderator':
        return tabs.filter(tab =>
          ['overview', 'modules', 'submissions', 'healthCheck'].includes(tab.id)
        );
      case 'admin':
        return tabs;
      default:
        return tabs.filter(tab => ['overview', 'modules'].includes(tab.id));
    }
  };

  if (isClientCapacityBlocked) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="mx-auto max-w-3xl rounded-md border border-gray-200 bg-white p-6 text-black shadow-sm">
          <h2 className="mb-2 text-xl font-semibold">Not available</h2>
          <p className="text-sm text-gray-700">This program is not available on the client side.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <div className="mb-2 flex items-center gap-2 md:gap-3">
            <AcademicCapIcon className="h-8 w-8 text-blue-600" />
            <h1 className="text-2xl font-bold text-black md:text-3xl">{originalContent.title}</h1>
          </div>
          <p className="text-sm text-black md:text-base">{originalContent.description}</p>
        </div>

        <div className="mb-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-blue-200 bg-blue-100 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <DocumentTextIcon className="h-5 w-5 text-blue-600" />
              <span className="text-sm text-black">Modules</span>
            </div>
            <p className="text-2xl font-bold text-black">
              {stats.completedModules}/{stats.totalModules}
            </p>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-100 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-green-600" />
              <span className="text-sm text-black">Lessons</span>
            </div>
            <p className="text-2xl font-bold text-black">
              {stats.completedLessons}/{stats.totalLessons}
            </p>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-100 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <StarIcon className="h-5 w-5 text-amber-600" />
              <span className="text-sm text-black">Rating</span>
            </div>
            <p className="text-2xl font-bold text-black">{stats.averageRating}</p>
          </div>
          <div className="rounded-lg border border-indigo-200 bg-indigo-100 p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5 text-indigo-600" />
              <span className="text-sm text-black">Participants</span>
            </div>
            <p className="text-2xl font-bold text-black">{stats.activeParticipants}</p>
          </div>
        </div>

        <div className="mb-8 rounded-lg border border-blue-200 bg-white shadow-sm">
          <div className="border-b border-blue-200">
            <nav className="flex flex-wrap gap-2 px-4 md:space-x-8 md:px-6">
              {getVisibleTabs().map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-black'
                      : 'border-transparent text-black hover:border-blue-300 hover:text-black'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-4 md:p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <h2 className="mb-4 flex items-center gap-2 text-xl font-semibold text-black">
                      <LightBulbIcon className="h-5 w-5" /> Course Overview
                    </h2>
                    <div className="prose max-w-none text-black">
                      <p className="mb-4">{originalContent.description}</p>
                    </div>

                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      <div className="rounded-lg bg-blue-50 p-6">
                        <h3 className="mb-3 text-lg font-medium text-black">Learning Objectives</h3>
                        <ul className="space-y-2 text-sm text-black">
                          {originalContent.modules.slice(0, 5).map((module, index) => (
                            <li key={index}>• {module.title}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="rounded-lg bg-blue-50 p-6">
                        <h3 className="mb-3 text-lg font-medium text-black">Course Features</h3>
                        <ul className="space-y-2 text-sm text-black">
                          <li>• Interactive video lessons with expert instructors</li>
                          <li>• Practical case studies and real-world examples</li>
                          <li>• Hands-on exercises and assignments</li>
                          <li>• Progress tracking and performance analytics</li>
                          <li>• Certificate upon completion</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'progress' && (
                  <motion.div
                    key="progress"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <ProgressTracker
                      programs={[selectedProgram]}
                      userProgress={userProgress}
                      onLessonStart={handleLessonStart}
                      onLessonComplete={handleLessonComplete}
                      userRole={userRole}
                    />
                  </motion.div>
                )}

                {activeTab === 'modules' && selectedProgram && (
                  <motion.div
                    key="modules"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <ModuleViewer
                      program={selectedProgram}
                      modules={modules}
                      userProgress={userProgress[selectedProgram.id]}
                      onLessonStart={handleLessonStart}
                      onLessonComplete={handleLessonComplete}
                      userRole={userRole}
                    />
                  </motion.div>
                )}

                {activeTab === 'submissions' && (
                  <motion.div
                    key="submissions"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <SubmissionManager
                      submissions={submissions}
                      selectedProgram={selectedProgram}
                      onCreateSubmission={handleSubmissionCreate}
                      onUpdateSubmission={handleSubmissionUpdate}
                      userRole={userRole}
                    />
                  </motion.div>
                )}

                {activeTab === 'healthCheck' && (
                  <motion.div
                    key="healthCheck"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <ProgramHealthCheckDashboard
                      userRole={userRole}
                      programId={selectedProgram?.id}
                    />
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramsManager;
